import { WithNativeHTMLProps } from "@kalyzee/kast-web-sdk/web-components";
import React, { ForwardedRef, useEffect, useMemo, useRef } from "react";

import { MeetingRoomStatus } from "@kalyzee/kast-web-sdk/websocket";
import { GroupRequestCard, ReactIcons, RequestCardContainer } from "@kalyzee/escaet-ui";
import { answerRoomModeratorCall } from "../../helpers/meeting";
import { playNotificationSoundEffect } from "../../helpers/sounds";
import { PopulatedMeetingData, PopulatedMeetingRoom } from "../../interfaces/populatedData";

export interface ModeratorCallsListProps extends WithNativeHTMLProps<HTMLDivElement> {
  meetingData?: PopulatedMeetingData;
  className?: string;
  style?: React.CSSProperties;
  onClose?: () => void;
}

export interface PendingInterventionsListRef {}

const ModeratorCallsList = React.forwardRef(
  ({ meetingData, className, style, onClose, nativeProps }: ModeratorCallsListProps, forwardRef: ForwardedRef<PendingInterventionsListRef>) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const previousCallingGroupsRef = useRef<PopulatedMeetingRoom[]>();
    const { ongoingGroups, callingGroups } = useMemo(() => {
      const ongoing = meetingData?.rooms.groups.filter((group) => group.room.status === MeetingRoomStatus.Ongoing) ?? [];
      const calling = ongoing.filter((group) => group.room.callingModerator);
      return { ongoingGroups: ongoing, callingGroups: calling };
    }, [meetingData]);

    // ---------------------------------------------------------------------- //
    // ---------------------------  UTILS ----------------------------- //
    // ---------------------------------------------------------------------- //

    const answerToRequest = async (roomId: string, accepted: boolean) => {
      await answerRoomModeratorCall(roomId, accepted);
    };

    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    useEffect(() => {
      if (previousCallingGroupsRef.current !== undefined) {
        callingGroups.forEach((g1) => {
          if (!previousCallingGroupsRef.current?.find((g2) => g1.id === g2.id)) {
            // new group
            playNotificationSoundEffect();
          }
        });
      }

      previousCallingGroupsRef.current = callingGroups;
    }, [callingGroups]);

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    if (!ongoingGroups?.length) return null;

    const classes = [];
    if (className) classes.push(className);

    return (
      <div {...(nativeProps ?? {})} className={classes.join(" ")} style={style} ref={containerRef}>
        <RequestCardContainer
          title="Appels en attente"
          Icon={ReactIcons.GroupMin}
          className="h-full w-full"
          onClose={onClose}
          cards={callingGroups.map((group, index) => (
            <GroupRequestCard
              key={index}
              name={`Groupe ${group.room.name ?? index + 1}`}
              onAccept={() => answerToRequest(group.id, true)}
              onCancel={() => answerToRequest(group.id, false)}
            />
          ))}
        />
      </div>
    );
  }
);

export default ModeratorCallsList;
